// Lazy-loads ffmpeg.wasm 0.12 (single-threaded — NO SharedArrayBuffer / COOP-COEP needed) and
// compresses a video. Self-hosted from /public/vendor/ffmpeg (UMD builds, loaded via <script>).
// Used by the Examination "Upload video" feature.
//
// Files in public/vendor/ffmpeg/:
//   ffmpeg.js         (@ffmpeg/ffmpeg 0.12 UMD  -> window.FFmpegWASM.FFmpeg)
//   814.ffmpeg.js     (the wrapper's web worker, referenced as classWorkerURL)
//   ffmpeg-util.js    (@ffmpeg/util 0.12 UMD    -> window.FFmpegUtil.fetchFile)
//   ffmpeg-core.js    (@ffmpeg/core 0.12, single-threaded)
//   ffmpeg-core.wasm  (~32 MB)

const ORIGIN = (typeof window !== "undefined" && window.location && window.location.origin)
    ? window.location.origin
    : "";
const BASE = ORIGIN + "/vendor/ffmpeg/";
// Version query busts stale browser cache — earlier attempts cached the multi-threaded 0.11
// files at these same URLs, causing "SharedArrayBuffer is not defined". Bump on any change.
const V = "?v=0.12.6";
const FF_MAIN = BASE + "ffmpeg.js" + V;
const FF_UTIL = BASE + "ffmpeg-util.js" + V;
const FF_CORE = BASE + "ffmpeg-core.js" + V;
const FF_WASM = BASE + "ffmpeg-core.wasm" + V;

let instancePromise = null;
let progressCb = null;

function loadScriptOnce(src, globalName) {
    return new Promise((resolve, reject) => {
        if (window[globalName]) { resolve(); return; }
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Failed to load the video compressor."));
        document.head.appendChild(s);
    });
}

async function getInstance() {
    if (instancePromise) return instancePromise;
    instancePromise = (async () => {
        await loadScriptOnce(FF_MAIN, "FFmpegWASM");
        await loadScriptOnce(FF_UTIL, "FFmpegUtil");
        const { FFmpeg } = window.FFmpegWASM;
        const ffmpeg = new FFmpeg();
        ffmpeg.on("progress", ({ progress }) => {
            if (progressCb && isFinite(progress)) {
                progressCb(Math.max(0, Math.min(1, progress)));
            }
        });
        // NOTE: do NOT pass classWorkerURL — with the UMD build that forces a {type:"module"}
        // worker, which has no importScripts and then fails to ESM-import the UMD core
        // ("failed to import ffmpeg-core.js"). Omitting it loads 814.ffmpeg.js as a classic
        // worker (resolved via webpack's public path = /vendor/ffmpeg/), which uses
        // importScripts(coreURL) correctly. coreURL/wasmURL are still passed explicitly.
        await ffmpeg.load({ coreURL: FF_CORE, wasmURL: FF_WASM });
        return ffmpeg;
    })();
    return instancePromise;
}

function extOf(name) {
    const m = /\.([a-z0-9]+)$/i.exec(name || "");
    return m ? "." + m[1].toLowerCase() : ".mp4";
}

/**
 * Compress `file` to the smallest reasonable mp4 for viewing a gait pattern: 480p (short side
 * capped at 480, orientation-safe, no upscaling), CRF 30, low-bitrate audio, trimmed to 30s.
 * Calls onProgress(0..1). Returns a Blob (mp4).
 */
export async function compressVideo(file, onProgress) {
    const ffmpeg = await getInstance();
    const { fetchFile } = window.FFmpegUtil;
    progressCb = typeof onProgress === "function" ? onProgress : null;

    const inName = "input" + extOf(file.name);
    const outName = "output.mp4";
    try {
        await ffmpeg.writeFile(inName, await fetchFile(file));
        await ffmpeg.exec([
            "-i", inName,
            "-t", "30",
            // Cap the short side at 480 (landscape or portrait), never upscale, then even dims.
            "-vf", "scale='if(gt(iw,ih),-2,min(480,iw))':'if(gt(iw,ih),min(480,ih),-2)',scale=trunc(iw/2)*2:trunc(ih/2)*2",
            "-c:v", "libx264", "-preset", "veryfast", "-crf", "30",
            "-c:a", "aac", "-b:a", "64k",
            "-movflags", "+faststart",
            outName,
        ]);
        const data = await ffmpeg.readFile(outName); // Uint8Array
        try { await ffmpeg.deleteFile(inName); await ffmpeg.deleteFile(outName); } catch (e) { /* noop */ }
        return new Blob([data.buffer], { type: "video/mp4" });
    } finally {
        progressCb = null;
    }
}
